import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import { logger,
         VerificationService,
         StudentPersonalInfoViewModel,
         VerificationStepsEnum,
         getMaxReviewTime,
         LogoComponent,
         PoweredByComponent,
         FormFooterComponent as FormFooter,
         getOptions,
         ProgramTheme,
         CountDownComponent,
         SSOPendingComponent,
         ReviewPendingComponent,
} from '@sheerid/jslib';

const DOC_UPLOAD_POLL_DURATION_DURING_COUNTDOWN = 5000;
const DOC_UPLOAD_POLL_DURATION = 20000;
const SSO_POLL_DURATION = 5000;

const defaultDocUploadSubtitle3 = 'You will be contacted via email.';

interface StepPendingProps {
    verificationService: VerificationService;
    intl: any;
}

const StepPending = ({ verificationService, intl }: StepPendingProps) => {
    const programId = verificationService.programId;
    const viewModel = verificationService.viewModel;
    const previousViewModel = (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
    const previousVerificationResponse = verificationService.previousVerificationResponse || undefined;
    const verificationId = verificationService.verificationResponse.verificationId;
    const getLogoUrl = (programTheme: ProgramTheme): ProgramTheme['logoUrl'] => {
        const options = getOptions();
        return options.hasOwnProperty('logoUrl') ? options.logoUrl : programTheme.logoUrl;
    };
    const hasLogo = getLogoUrl(verificationService.programTheme) ? true : false;
    const countdownSeconds = 20;

    const [showCountDown, setShowCountDown] = React.useState(false);

    const getLogo = () => (
        hasLogo ? (
            <LogoComponent verificationService={verificationService} />
        ) : (
            <img
                className="sid-l-lead-image"
                alt="design element"
                src="https://assets-resources.sheerid.com/common/images/2018/icons/doc-pending.svg"
            />
        )
    );

    const isPreviousStepSSO = (): boolean => {
        if (previousVerificationResponse && previousVerificationResponse.currentStep === VerificationStepsEnum.sso) {
            return true;
        }
        return false;
    };

    const cancelSSOStep = () => {
        const nextState = produce(viewModel, () => {});
        logger.info('StepSSOComponent submitting cancel request');
        clearTimeout(timerRef.current);
        verificationService.submitStep(VerificationStepsEnum.sso, nextState, verificationService.previousVerificationResponse);
    };

    const pollForVerificationServiceUpdates = (timer: number) => setTimeout(
        () => {
            timerRef.current = pollForVerificationServiceUpdates(timer);
            verificationService.fetchExistingVerificationRequest(programId, verificationId, previousVerificationResponse, previousViewModel, false);
        },
        timer,
    );

    const timerRef = React.useRef(null);

    React.useEffect(() => {
        const timer = isPreviousStepSSO() ? SSO_POLL_DURATION : DOC_UPLOAD_POLL_DURATION_DURING_COUNTDOWN;
        timerRef.current = pollForVerificationServiceUpdates(timer);
        return () => clearTimeout(timerRef.current);
    },              []);

    const onCountdownFinish = () => {
        setShowCountDown(false);
        clearTimeout(timerRef.current);
        timerRef.current = pollForVerificationServiceUpdates(DOC_UPLOAD_POLL_DURATION);
    };

    const logo = getLogo();

    return (
        <div id="sid-step-pending" className="sid-l-container">
            { isPreviousStepSSO() ? (
                <SSOPendingComponent logo={logo} cancelSSO={cancelSSOStep} />
            ) : showCountDown ? (
                <CountDownComponent logo={logo} onCountdownFinish={onCountdownFinish} countdownSeconds={countdownSeconds} />
            ) : (
                <ReviewPendingComponent logo={logo} intl={intl} maxReviewTime={getMaxReviewTime(verificationService.programTheme)} />
            )}

            { !showCountDown ? (
                <FormFooter verificationService={verificationService}>
                    <FormattedHTMLMessage
                        id="studentInfoShared"
                        values={{
                            companyName: verificationService.programTheme.intl.messages.companyName || '{Company}',
                        }}
                        defaultMessage={'Information entered here will be used for verification purposes. '}
                    />
                    {
                        previousViewModel && previousViewModel.countryChoice && previousViewModel.countryChoice.value !== 'US' ?
                            <FormattedHTMLMessage
                                id="informationTransferredToUS"
                                defaultMessage=
                                    "Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy."
                            />
                        : null
                    }
                </FormFooter>
            ) : (
                <PoweredByComponent verificationService={verificationService} isCentered />
            )
        }
            <div className="sid-footer__subtitle sid-l-space-top-md">
                <FormattedHTMLMessage
                    id="step.pending.subtitle3"
                    defaultMessage={defaultDocUploadSubtitle3}
                />
            </div>
        </div>
    );
};

export const StepPendingComponent = injectIntl(StepPending);
